'use client';

import Link from 'next/link';
import type { ShowDetailRecord } from '../../_lib/detail/show-detail-types';
import { getStarValue, formatReviewCount } from '../../../_lib/shows/reviewFormat';
import { MediaGallery } from './MediaGallery';
import { ClampedText } from './ClampedText';
import { SHOWS_BASE_PATH } from '../../config';

interface ShowDetailHeaderProps {
  show: ShowDetailRecord;
  /** Desktop anchor-scrolls to the calendar (default browser `#` behavior);
   *  mobile opens the calendar as a bottom-sheet overlay instead — wired by
   *  the page assembly once `TicketBookingPanel` exists (FR-HEAD-09). */
  onBuyTickets?: () => void;
}

const TITLE_LIMIT = 60;

/**
 * Show Information Header — `brd-frontend.md` §2, `frd.md` FR-HEAD-01..09.
 * Reuses `getStarValue`/`formatReviewCount` from `_lib/shows/reviewFormat.ts`
 * (already shared by the listing page + Nearby Show cards) rather than the
 * "simple proportional fill" this domain's own `build-plan.md` had
 * originally proposed — that shared utility already encodes a deliberate,
 * previously-decided rating-display rule (see its own doc comment), and
 * reusing it keeps the whole Shows domain visually consistent rather than
 * re-litigating a decision already made elsewhere in this codebase.
 */
export function ShowDetailHeader({ show, onBuyTickets }: ShowDetailHeaderProps) {
  const { event, reviews, breadcrumb, performances } = show;

  const isSoldOut =
    !performances ||
    performances.length === 0 ||
    performances.every(
      (p) => !p.isBookable || (p.availableTickets != null && p.availableTickets <= 0)
    );

  const rawTitle = event.name || '';
  const isLongTitle = rawTitle.length > TITLE_LIMIT;
  const displayTitle = isLongTitle ? `${rawTitle.slice(0, TITLE_LIMIT)}…` : rawTitle || 'Show Name is Unavailable';

  const locationLine = event.theater || 'Location Unavailable';

  const hasRating = reviews != null && reviews.averageRating > 0;
  const hasReviews = reviews != null && reviews.totalReviews > 0;
  const starValue = hasRating ? getStarValue(reviews!.averageRating) : 0;
  const fullStars = Math.floor(starValue);
  const hasHalfStar = starValue % 1 !== 0;

  return (
    <>
      {/* Full-bleed colored band — same markup/classes as the Privacy Policy
       * page's breadcrumb (`app/privacy-policy/PrivacyPolicyClient.tsx`):
       * `container-fluid` band → `.container` → `.row` → `.col-lg-12` →
       * a real Bootstrap `nav > ol.breadcrumb > li.breadcrumb-item` (global
       * `app/styles/breadcrumb.css` supplies the "/" separators + active
       * item color) instead of a hand-rolled flex `<nav>` with manual
       * `<span>/</span>` dividers. */}
      <section className="container-fluid mb-4" style={{ backgroundColor: '#dc9cf7' }}>
        <div className="">
          <div className="">
            <div className="shows-container">
              <div className="row">
                <div className="col-lg-12">
                  <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                      <li className="breadcrumb-item">
                        <Link href={SHOWS_BASE_PATH}>Home</Link>
                      </li>
                      <li className="breadcrumb-item">
                        <Link href={SHOWS_BASE_PATH}>{breadcrumb.city}</Link>
                      </li>
                      <li className="breadcrumb-item active" aria-current="page">
                        {breadcrumb.showName}
                      </li>
                    </ol>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="shows-container">
      <section className="shows-detail-card shows-detail-header-card">
        <div className="shows-detail-header-grid">
          <div className="shows-detail-header-info">
            <div className="shows-detail-header-location">
              <i className="fa-regular fa-location-dot" aria-hidden /> {locationLine}
            </div>

            <div title={isLongTitle ? rawTitle : undefined} className="shows-detail-header-title-wrap">
              <h1 className="shows-detail-header-title">{displayTitle}</h1>
            </div>

            {(hasRating || hasReviews) && (
              <div className="shows-detail-header-reviews">
                {hasRating ? (
                  <>
                    <span className="font-semibold text-orange rating-txt">{reviews!.averageRating.toFixed(1)}</span>
                    <span className="shows-detail-header-stars">
                      {Array.from({ length: 5 }, (_, i) => {
                        if (i < fullStars) return <i key={i} className="fas fa-star pl-1" />;
                        if (i === fullStars && hasHalfStar) return <i key={i} className="fas fa-star-half-alt pl-1" />;
                        return <i key={i} className="fal fa-star" />;
                      })}
                    </span>
                    {hasReviews && <span className="shows-detail-header-review-count text-15">{formatReviewCount(reviews!.totalReviews)}</span>}
                  </>
                ) : (
                  <span className="shows-detail-header-review-count text-15">{formatReviewCount(reviews!.totalReviews)}</span>
                )}
              </div>
            )}

            {event.tagLine && <ClampedText text={event.tagLine} className="shows-detail-header-tagline" />}

            <div className="shows-detail-header-cta">
              {onBuyTickets ? (
                <button
                  type="button"
                  className="shows-detail-buy-tickets"
                  onClick={onBuyTickets}
                  disabled={isSoldOut}
                >
                  Buy Tickets
                </button>
              ) : isSoldOut ? (
                <button
                  type="button"
                  className="shows-detail-buy-tickets"
                  disabled
                >
                  Buy Tickets
                </button>
              ) : (
                <a href="#ticket-booking-panel" className="shows-detail-buy-tickets">
                  Buy Tickets
                </a>
              )}
            </div>
          </div>

          <div className="shows-detail-header-media">
            <MediaGallery
              primaryImage={event.images.detail}
              secondaryImages={event.images.gallery}
              trailerUrl={event.videos[0]?.Url || null}
            />
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
